import React from "react";
import { useDispatch } from "react-redux";
import {
  useGetGiftcardTransactionDetailsQuery,
  useRetryGiftcardTransactionMutation,
  useRetryGiftcardTransactionWithOtpMutation,
} from "../../../features/giftcards/giftcardsSlice";
import { openOtpModal } from "../../../features/withdraw-otp/withdrawOtpSlice";
import { WithdrawOtpModal } from "../withdraw-now/WithdrawOtpModal";
import { format } from "date-fns";
import { toast } from "react-hot-toast";

interface GiftcardTransactionDetailsProps {
  requestId: string;
  onBack: () => void;
}

export const GiftcardTransactionDetails: React.FC<GiftcardTransactionDetailsProps> = ({
  requestId,
  onBack,
}) => {
  const dispatch = useDispatch();
  const { data: transaction, isLoading, error, refetch } =
    useGetGiftcardTransactionDetailsQuery(requestId);
  const [retry, { isLoading: retryLoading }] = useRetryGiftcardTransactionMutation();
  const [retryWithOtp] = useRetryGiftcardTransactionWithOtpMutation();

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
        return "bg-green-500/20 text-green-500";
      case "failed":
        return "bg-red-500/20 text-red-500";
      case "processing":
      case "queued":
      case "initiated":
        return "bg-yellow-500/20 text-yellow-500";
      default:
        return "bg-gray-500/20 text-gray-400";
    }
  };

  const formatNGN = (value: number) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
    }).format(value);
  };

  const getCurrencySymbol = (currencyCode: string) => {
    const symbols: Record<string, string> = {
      USD: "$",
      NGN: "₦",
      EUR: "€",
      GBP: "£",
      INR: "₹",
      CAD: "CA$",
      AUD: "A$",
      JPY: "¥",
      CNY: "¥",
      KRW: "₩",
      BRL: "R$",
      MXN: "MX$",
      ZAR: "R",
      AED: "د.إ",
      SAR: "﷼",
    };
    return symbols[currencyCode] || currencyCode + " ";
  };

  const formatRecipientAmount = () => {
    if (!transaction) return "";
    const symbol = getCurrencySymbol(transaction.recipientCurrencyCode);
    const amount = transaction.recipientAmount / transaction.quantity;
    return `${symbol}${amount.toLocaleString()} ${transaction.recipientCurrencyCode}`;
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const handleRetry = async () => {
    try {
      await retry(requestId).unwrap();
      toast.success("Transaction retry initiated");
      refetch();
    } catch (err: any) {
      if (
        err?.status === 401 &&
        err?.data?.message?.includes("OTP required") &&
        err?.data?.challengeId &&
        err?.data?.instructions
      ) {
        dispatch(
          openOtpModal({
            challengeId: err.data.challengeId,
            ttlSeconds: err.data.instructions.ttlSeconds || 300,
          })
        );
        toast.error("OTP verification required. Check your email.");
      } else {
        toast.error(err?.data?.message || "Failed to retry transaction");
      }
    }
  };

  const handleRetryOtpSubmit = async (challengeId: string, otpCode: string) => {
    await retryWithOtp({ requestId, challengeId, otpCode }).unwrap();
    toast.success("Transaction retry initiated");
    refetch();
  };

  if (isLoading) {
    return (
      <div className="bg-black px-4 py-5 mt-6 rounded-lg">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-800 rounded w-1/3" />
          <div className="h-32 bg-gray-800 rounded" />
          <div className="h-24 bg-gray-800 rounded" />
        </div>
      </div>
    );
  }

  if (error || !transaction) {
    return (
      <div className="bg-black px-4 py-5 mt-6 rounded-lg">
        <button
          onClick={onBack}
          className="text-primary hover:underline mb-4 flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to History
        </button>
        <div className="text-red-500 text-center py-8">
          Failed to load transaction details.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black px-4 py-5 mt-6 rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={onBack}
          className="text-primary hover:underline flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to History
        </button>
        <span className={`px-3 py-1 rounded-full text-sm ${getStatusColor(transaction.status)}`}>
          {transaction.status}
        </span>
      </div>

      {/* Product Info */}
      <div className="bg-gray-800 rounded-lg p-4 mb-4">
        <h3 className="text-white text-lg font-medium">{transaction.brandName}</h3>
        <p className="text-gray-400 text-sm">{transaction.productName}</p>
        <p className="text-gray-400/70 text-xs mt-2">
          {format(new Date(transaction.created_at), "MMMM d, yyyy 'at' h:mm a")}
        </p>
      </div>

      {/* Order Summary */}
      <div className="bg-gray-800 rounded-lg p-4 mb-4">
        <h4 className="text-white font-medium mb-3">Order Summary</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-400">Gift Card Value</span>
            <span className="text-white">{formatRecipientAmount()} x {transaction.quantity}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Amount (NGN)</span>
            <span className="text-white">{formatNGN(transaction.amountNGN)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Service Fee</span>
            <span className="text-white">{formatNGN(transaction.gcbuyingFeeNGN)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Processing Fee</span>
            <span className="text-white">{formatNGN(transaction.reloadlyFeeNGN)}</span>
          </div>
          <div className="border-t border-gray-700 my-2" />
          <div className="flex justify-between text-lg">
            <span className="text-white font-medium">Total Paid</span>
            <span className="text-primary font-bold">{formatNGN(transaction.totalNGN)}</span>
          </div>
        </div>
      </div>

      {/* Gift Card Codes - Only show for completed transactions */}
      {transaction.status === "completed" && transaction.giftcardCodes && transaction.giftcardCodes.length > 0 && (
        <div className="bg-gray-800 rounded-lg p-4 mb-4">
          <h4 className="text-white font-medium mb-3">
            Gift Card{transaction.giftcardCodes.length > 1 ? "s" : ""} ({transaction.giftcardCodes.length})
          </h4>
          <div className="space-y-3">
            {transaction.giftcardCodes.map((card, index) => (
              <div key={index} className="bg-gray-900 rounded-lg p-3">
                {transaction.giftcardCodes!.length > 1 && (
                  <p className="text-gray-500 text-xs mb-2">Card {index + 1}</p>
                )}

                {/* Card Number / Code */}
                <div className="mb-2">
                  <label className="text-gray-400 text-xs block mb-1">Card Code</label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-gray-800 px-3 py-2 rounded text-primary font-mono text-sm break-all">
                      {card.cardNumber}
                    </code>
                    <button
                      onClick={() => copyToClipboard(card.cardNumber, "Card code")}
                      className="p-2 bg-gray-700 hover:bg-gray-600 rounded transition-colors"
                      title="Copy code"
                    >
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* PIN (if available) */}
                {card.pinCode && (
                  <div className="mb-2">
                    <label className="text-gray-400 text-xs block mb-1">PIN</label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-gray-800 px-3 py-2 rounded text-white font-mono text-sm">
                        {card.pinCode}
                      </code>
                      <button
                        onClick={() => copyToClipboard(card.pinCode!, "PIN")}
                        className="p-2 bg-gray-700 hover:bg-gray-600 rounded transition-colors"
                        title="Copy PIN"
                      >
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                {card.redemptionUrl && (
                  <div className="mb-2">
                    <label className="text-gray-400 text-xs block mb-1">Redemption URL</label>
                    <div className="flex items-center gap-2">
                      <a
                        href={card.redemptionUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 bg-gray-800 px-3 py-2 rounded text-blue-400 hover:text-blue-300 text-sm break-all underline"
                      >
                        {card.redemptionUrl}
                      </a>
                      <button
                        onClick={() => copyToClipboard(card.redemptionUrl!, "Redemption URL")}
                        className="p-2 bg-gray-700 hover:bg-gray-600 rounded transition-colors"
                        title="Copy redemption URL"
                      >
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Redeem Instructions */}
          {transaction.redeemInstructions && (
            <div className="mt-4 pt-4 border-t border-gray-700">
              <p className="text-gray-400 text-sm">
                <strong className="text-white">Redeem Instructions:</strong><br />
                {transaction.redeemInstructions}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Processing Status */}
      {(transaction.status === "processing" || transaction.status === "queued" || transaction.status === "initiated") && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="animate-spin">
              <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
            <div>
              <p className="text-yellow-500 font-medium">Processing your order</p>
              <p className="text-gray-400 text-sm">
                Your gift card purchase is being processed. This usually takes a few seconds.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Failed Status */}
      {transaction.status === "failed" && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-red-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="text-red-500 font-medium">Purchase Failed</p>
              {transaction.failureReason && (
                <p className="text-gray-400 text-sm mt-1">{transaction.failureReason}</p>
              )}
              {transaction.refunded && (
                <p className="text-green-500 text-sm mt-2">
                  Your wallet has been refunded {formatNGN(transaction.totalNGN)}.
                </p>
              )}
            </div>
          </div>

          {/* Retry Button */}
          {!transaction.refunded && (
            <button
              onClick={handleRetry}
              disabled={retryLoading}
              className="mt-4 w-full px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {retryLoading ? "Retrying..." : "Retry Purchase"}
            </button>
          )}
        </div>
      )}

      {/* Transaction Reference */}
      <div className="bg-gray-800 rounded-lg p-4">
        <h4 className="text-white font-medium mb-3">Transaction Details</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-400">Reference ID</span>
            <button
              onClick={() => copyToClipboard(transaction.request_id, "Reference ID")}
              className="text-primary hover:underline font-mono text-xs"
            >
              {transaction.request_id}
            </button>
          </div>
          {transaction.reloadlyTransactionId && (
            <div className="flex justify-between">
              <span className="text-gray-400">Reloadly Transaction</span>
              <span className="text-gray-300 font-mono text-xs">{transaction.reloadlyTransactionId}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-gray-400">Recipient Email</span>
            <span className="text-gray-300">{transaction.recipientEmail}</span>
          </div>
        </div>
      </div>

      <WithdrawOtpModal onOtpSubmit={handleRetryOtpSubmit} />
    </div>
  );
};
